/**
 * Mobile Cart Rescue - Native Web Component
 * Mobile cart abandonment exit/hesitation popup for TarpsPlus
 * Supports Shopify Theme Editor Live Design Mode (Shopify.designMode)
 */

class MobileCartRescue extends HTMLElement {
  connectedCallback() {
    this.delayMs = (parseInt(this.dataset.delaySeconds, 10) || 25) * 1000;
    this.frequencyMs = (parseInt(this.dataset.frequencyHours, 10) || 24) * 3600 * 1000;
    this.couponCode = this.dataset.couponCode || 'MOBILE5';
    this.isDesignMode = this.dataset.designMode === 'true' || (window.Shopify && window.Shopify.designMode);
    this.sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '') || '';

    this.backdrop = this.querySelector('.mobile-cart-rescue__backdrop');
    this.dialog = this.querySelector('.mobile-cart-rescue__dialog');
    this.shown = false;
    this.timer = null;

    this._initListeners();

    if (this.isDesignMode) {
      this.show(true);
      return;
    }

    if (!this._isMobileDevice() || this._isFrequencyCapped()) {
      return;
    }

    this._startInactivityTimer();
  }

  disconnectedCallback() {
    this._clearInactivityTimer();
  }

  _isMobileDevice() {
    if (this.isDesignMode) return true;
    return window.innerWidth <= 768 || ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  }

  _isFrequencyCapped() {
    if (this.isDesignMode) return false;
    try {
      const lastShown = localStorage.getItem('mobile_cart_rescue_ts');
      if (lastShown) {
        const elapsed = Date.now() - parseInt(lastShown, 10);
        return elapsed < this.frequencyMs;
      }
    } catch (e) {}
    return false;
  }

  _markFrequencyCap() {
    if (this.isDesignMode) return;
    try {
      localStorage.setItem('mobile_cart_rescue_ts', Date.now().toString());
    } catch (e) {}
  }

  _initListeners() {
    this.addEventListener('click', (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.dataset.action;
      if (action === 'close') {
        this.hide();
      } else if (action === 'checkout') {
        this.handleCheckout();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.shown) {
        this.hide();
      }
    });

    // Shopify Theme Editor Design Mode Event Listeners for Live Visual Preview
    document.addEventListener('shopify:section:select', (e) => {
      if (e.detail?.sectionId === this.sectionId || this.contains(e.target) || e.target?.contains?.(this)) {
        this.show(true);
      }
    });

    document.addEventListener('shopify:section:deselect', (e) => {
      if (e.detail?.sectionId === this.sectionId || this.contains(e.target) || e.target?.contains?.(this)) {
        this.hide();
      }
    });

    document.addEventListener('shopify:section:load', (e) => {
      if (e.detail?.sectionId === this.sectionId || this.contains(e.target) || e.target?.contains?.(this)) {
        this.show(true);
      }
    });

    document.addEventListener('shopify:block:select', (e) => {
      if (this.contains(e.target) || e.target?.contains?.(this)) {
        this.show(true);
      }
    });

    // Mobile visibility / tab switch hesitation trigger
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && !this.shown && !this.isDesignMode) {
        this._verifyAndShow();
      }
    });

    // PubSub / Cart Drawer Integration if available
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const count = event?.cartData?.item_count ?? 0;
        if (count > 0 && this._isCartContext()) {
          this._restartInactivityTimer();
        }
      });
    }
  }

  _isCartContext() {
    if (this.isDesignMode) return true;
    const isCartPage = window.location.pathname.includes('/cart');
    const isDrawerOpen = document.querySelector('cart-drawer.is-empty') === null && 
                         (document.querySelector('cart-drawer[active]') !== null || 
                          document.querySelector('.cart-drawer.active') !== null ||
                          document.querySelector('cart-drawer.active') !== null);
    return isCartPage || isDrawerOpen;
  }

  _startInactivityTimer() {
    if (this.isDesignMode) return;
    this._clearInactivityTimer();
    this.timer = setTimeout(() => {
      this._verifyAndShow();
    }, this.delayMs);
  }

  _restartInactivityTimer() {
    if (!this.shown && !this.isDesignMode) {
      this._startInactivityTimer();
    }
  }

  _clearInactivityTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  async _verifyAndShow() {
    if (this.shown || this._isFrequencyCapped() || this.isDesignMode) return;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (cart && cart.item_count > 0 && this._isCartContext()) {
        this.show();
      }
    } catch (err) {
      if (this._isCartContext()) {
        this.show();
      }
    }
  }

  show(isDesignModePreview = false) {
    if (this.shown && !isDesignModePreview) return;
    this.shown = true;
    if (!isDesignModePreview) {
      this._markFrequencyCap();
      this._clearInactivityTimer();
    }

    this.classList.add('is-active');
    this.setAttribute('aria-hidden', 'false');
    if (!isDesignModePreview) {
      document.body.classList.add('mcr-scroll-lock');
    }

    if (!isDesignModePreview) {
      this._dispatchAnalyticsEvent('mobile_cart_rescue_impression');
    }
  }

  hide() {
    this.shown = false;
    this.classList.remove('is-active');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mcr-scroll-lock');
  }

  async handleCheckout() {
    if (this.isDesignMode) return;

    this._dispatchAnalyticsEvent('mobile_cart_rescue_cta_click');

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { 'Mobile Cart Rescue': 'Rescued' }
        })
      });
    } catch (e) {}

    const redirectUrl = `/discount/${encodeURIComponent(this.couponCode)}?redirect=/checkout`;
    window.location.href = redirectUrl;
  }

  _dispatchAnalyticsEvent(eventName) {
    if (this.isDesignMode) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      campaign: 'Mobile Cart Rescue',
      coupon: this.couponCode
    });

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(eventName, {
        campaign: 'Mobile Cart Rescue',
        coupon: this.couponCode
      });
    }
  }
}

if (!customElements.get('mobile-cart-rescue')) {
  customElements.define('mobile-cart-rescue', MobileCartRescue);
}
